import React, { Suspense, lazy, useEffect, useState } from 'react';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { BrowserRouter, Navigate, NavLink, Route, Routes } from 'react-router-dom';
import LoginPage from './components/LoginPage';
import OAuthCallbackPage from './components/OAuthCallbackPage';
import ConnectPage from './components/ConnectPage';
import RecommendationsPage from './components/RecommendationsPage';
import ActivityPage from './components/ActivityPage';
import PerformancePage from './components/PerformancePage';
import AdminPage from './components/AdminPage';
import ReferralsPage from './components/ReferralsPage';
import CreateCampaignPage from './components/AiCampaignCreatorPage';
import { apiErrorMessage, loginWithGoogle } from './api/adpilotApi';
import { clearSession, loadSession, saveSession } from './utils/session';

// Campaign conversation with text and voice, using the existing AdPilot agent.
const AgentPage = lazy(() => import('./agent/AgentPage'));

const previewMode = process.env.NODE_ENV === 'development'
  && process.env.REACT_APP_SKIP_LOGIN === 'true';
const previewUser = {
  id: 'local-preview',
  name: "Dave's Garage",
  email: 'Local preview',
  preview: true,
};

export const isAdpilotAdmin = (email) => ['wowai.work', 'stellair.ai']
  .includes(String(email || '').trim().toLowerCase().split('@')[1]);

function Shell({ user, onLogout, basePath = '' }) {
  const navClass = ({ isActive }) => `nav-item ${isActive ? 'nav-item-active' : ''}`;
  const route = (path) => `${basePath}${path}`;
  const admin = !user.preview && isAdpilotAdmin(user.email);

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="brand">
          <span className="brand-mark">A</span>
          <span>AdPilot</span>
        </div>
        <nav className="nav-list" aria-label="Primary navigation">
          {!user.preview && <NavLink to="/agent" className={navClass}>
            <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="9" y="2" width="6" height="13" rx="3" /><path d="M5 10v2a7 7 0 0 0 14 0v-2M12 19v3m-4 0h8" /></svg>
            Talk to AdPilot
          </NavLink>}
          <NavLink to={route('/create')} className={navClass}>
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>
            Create
          </NavLink>
          <NavLink to={route('/recommendations')} className={navClass}>
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 15.5 8.5 11l3 3L20 5.5M20 10V5h-5" /></svg>
            Brief
          </NavLink>
          <NavLink to={route('/performance')} className={navClass}>
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 20V10m8 10V4m8 16V8" /></svg>
            Performance
          </NavLink>
          <NavLink to={route('/activity')} className={navClass}>
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 8v4l2.5 2.5M21 12a9 9 0 1 1-3-6.7" /></svg>
            Activity
          </NavLink>
          <NavLink to={route('/connect')} className={navClass}>
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 12h8M12 8v8M5 4h14a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1Z" /></svg>
            Sources
          </NavLink>
          {!user.preview && (
            <NavLink to={route('/referrals')} className={navClass}>
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm8 2a3 3 0 1 0 0-6M2 20a6 6 0 0 1 12 0m1-5a5 5 0 0 1 7 5" /></svg>
              Network
            </NavLink>
          )}
          {admin && (
            <NavLink to={route('/admin')} className={navClass}>
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 19V9M10 19V5M16 19v-7M22 19V3" /></svg>
              Admin
            </NavLink>
          )}
        </nav>
        <div className="sidebar-user">
          <span className="avatar">{(user.name || user.email || 'A').charAt(0).toUpperCase()}</span>
          <span className="user-copy">
            <strong>{user.name || 'Your workspace'}</strong>
            <small>{user.email}</small>
          </span>
          {!user.preview && (
            <button type="button" className="logout-button" onClick={onLogout} aria-label="Log out" title="Log out">
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M10 17l5-5-5-5M15 12H3M14 4h5a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-5" /></svg>
            </button>
          )}
        </div>
      </aside>
      <main className="app-main">
        {basePath && <div className="demo-banner">Demo workspace <span>Sample data only</span><a href="/login">Sign in</a></div>}
        <Routes>
          <Route path="create" element={<CreateCampaignPage user={user} />} />
          <Route path="connect" element={<ConnectPage user={user} />} />
          <Route path="recommendations" element={<RecommendationsPage user={user} />} />
          <Route path="performance" element={<PerformancePage user={user} />} />
          <Route path="activity" element={<ActivityPage user={user} />} />
          {!user.preview && <Route path="referrals" element={<ReferralsPage />} />}
          {admin && <Route path="admin" element={<AdminPage />} />}
          <Route path="*" element={<Navigate to={route('/recommendations')} replace />} />
        </Routes>
      </main>
    </div>
  );
}

function AppInner() {
  const [user, setUser] = useState(() => (previewMode ? previewUser : loadSession()));
  const [loginError, setLoginError] = useState(null);

  // Session expiry and cross-tab logout: re-check every minute and react to
  // other tabs clearing the session key.
  useEffect(() => {
    if (previewMode) return undefined;
    const onStorage = () => setUser(loadSession());
    window.addEventListener('storage', onStorage);
    const timer = setInterval(() => {
      if (user && (!user.expiresAt || Date.now() > user.expiresAt)) {
        clearSession();
        setUser(null);
      }
    }, 60 * 1000);
    return () => {
      window.removeEventListener('storage', onStorage);
      clearInterval(timer);
    };
  }, [user]);

  const handleLoginSuccess = async (credential, invitationCode) => {
    setLoginError(null);
    try {
      const { user: backendUser, sessionToken, expiresAt } = await loginWithGoogle(credential, invitationCode);
      const session = saveSession({ ...backendUser, sessionToken, expiresAt });
      setUser(session);
    } catch (error) {
      setLoginError(apiErrorMessage(error, 'Login failed'));
    }
  };

  const handleLogout = () => {
    clearSession();
    setUser(null);
  };

  // B2C self-serve: a valid platform login is enough. There is no Stellair
  // account id in this product.
  const ready = Boolean(user && user.id);

  return (
    <BrowserRouter>
      <Routes>
        <Route
          path="/agent"
          element={
            ready
              ? <Suspense fallback={null}><AgentPage /></Suspense>
              : <Navigate to="/login" replace />
          }
        />
        {/* Demo temporarily disabled; retain the route for re-enabling later.
        <Route path="/demo/*" element={<Shell user={previewUser} basePath="/demo" />} />
        */}
        <Route
          path="/login"
          element={
            ready
              ? <Navigate to="/recommendations" replace />
              : <LoginPage onLoginSuccess={handleLoginSuccess} loginError={loginError} />
          }
        />
        <Route path="/oauth" element={<OAuthCallbackPage />} />
        <Route
          path="*"
          element={
            ready
              ? <Shell user={user} onLogout={handleLogout} />
              : <Navigate to="/login" replace />
          }
        />
      </Routes>
    </BrowserRouter>
  );
}

function App() {
  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  // Demo temporarily disabled along with its OAuth-provider bypass.
  // if (window.location.pathname.startsWith('/demo')) return <AppInner />;
  if (!clientId) {
    return (
      <div className="center-screen">
        <p className="text-red-600" role="alert">
          REACT_APP_GOOGLE_CLIENT_ID is not configured.
        </p>
      </div>
    );
  }
  return (
    <GoogleOAuthProvider clientId={clientId}>
      <AppInner />
    </GoogleOAuthProvider>
  );
}

export default App;
